// components/PreviewModal.tsx
import React, { useEffect } from 'react';
import styled from 'styled-components';
import { CanvasItem, TempSettings, MenuItem } from '../../types/types';
import { useTheme } from '../../middleware/ThemeContext';

import CloseIcon from '../../assets/icons/CloseIcon';
import PreviewHeader from './PreviewHeader';
import PreviewCanvas from './PreviewCanvas';
import PreviewFooter from './PreviewFooter';
import { Theme } from '../../utils/themes';
import StyledText from '../StyledComponents/StyledText';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  padding: 20px;
`;

const ModalContent = styled.div<{ theme: Theme }>`
  background: ${({ theme }) => theme.CorporateTheme.colors.background};
  border-radius: 15px;
  width: 100%;
  max-width: 1400px;
  max-height: 95vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  overflow: hidden;
`;

const ModalHeader = styled.div<{ theme: Theme }>`
  padding: 20px 30px;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
    background: ${({ theme }) => theme.CorporateTheme.colors.background};

`;


const CloseButton = styled.button<{ theme: Theme }>`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: background 0.2s;
 
`;

const PreviewContentWrapper = styled.div<{ theme: Theme }>`
  flex: 1;
  overflow-y: auto;
  background: ${({ theme }) => theme.CorporateTheme.colors.background};
  padding: 30px;
`;

const PreviewPageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  background: white;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  border-radius: 11px;
`;

interface PreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  pageName?: string;
  canvasItems: CanvasItem[];
  headerSettings: TempSettings;
  footerSettings: TempSettings;
  pendingChanges: Map<string, TempSettings>;
  defaultHeaderSettings: any;
  defaultFooterSettings: any;
  headerMenuItems?: MenuItem[];
  footerMenuItems?: MenuItem[];
  primaryFont?: string;
  customCss?: string;
  customJs?: string;
}

const PreviewModal: React.FC<PreviewModalProps> = ({
  isOpen,
  onClose,
  pageName,
  canvasItems,
  headerSettings,
  footerSettings,
  pendingChanges,
  defaultHeaderSettings,
  defaultFooterSettings,
  headerMenuItems = [],
  footerMenuItems = [],
  primaryFont,
  customCss = ''
}) => {
  const { theme } = useTheme();
  const previewContentRef = React.useRef<HTMLDivElement | null>(null);

  // Inject custom CSS with high priority for preview
  useEffect(() => {
    if (isOpen && customCss && customCss.trim()) {
      try {
        const style = document.createElement('style');
        style.setAttribute('type', 'text/css');
        style.setAttribute('data-custom-css', 'preview-modal');
        style.textContent = customCss;

        // Append to head (after other styles for higher priority)
        document.head.appendChild(style);

        return () => {
          const existingStyle = document.querySelector('style[data-custom-css="preview-modal"]');
          if (existingStyle) {
            existingStyle.remove();
          }
        };
      } catch (error) {
        console.error('Error injecting custom CSS in preview:', error);
      }
    }
  }, [isOpen, customCss]);

  useEffect(() => {
    if (!isOpen) return;
    previewContentRef.current?.scrollTo({ top: 0, behavior: 'auto' });
  }, [isOpen]);

  if (!isOpen) return null;

  // Merge header and footer settings with defaults and pending changes
  const finalHeaderSettings = {
    ...defaultHeaderSettings,
    ...(pendingChanges.get('page-header') || {}),
    ...headerSettings
  };

  const finalFooterSettings = {
    ...defaultFooterSettings,
    ...(pendingChanges.get('page-footer') || {}),
    ...footerSettings
  };

  // Remove _meta from settings
  delete finalHeaderSettings._meta;
  delete finalFooterSettings._meta;

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <ModalOverlay onClick={handleOverlayClick}
      style={{ backgroundColor: 'rgba(231, 224, 224, 1)', overflow: 'hidden' }}>
      <ModalContent theme={theme}>
        <ModalHeader theme={theme}>
          <StyledText fontSize="20px" fontWeight={700} textColor={theme.CorporateTheme.colors.primary}>{pageName || 'Page Preview'}</StyledText>
          <CloseButton onClick={onClose} theme={theme}>
            <CloseIcon color={theme.CorporateTheme.colors.gunmetal} />
          </CloseButton>
        </ModalHeader>

        <PreviewContentWrapper ref={previewContentRef} theme={theme}>
          <PreviewPageContainer>
            <PreviewHeader
              settings={finalHeaderSettings}
              menuItems={headerMenuItems}
              primaryFont={primaryFont}
            />
            <PreviewCanvas
              canvasItems={canvasItems}
              pendingChanges={pendingChanges}
              primaryFont={primaryFont}
            />
            <PreviewFooter settings={finalFooterSettings} menuItems={footerMenuItems} primaryFont={primaryFont} />
          </PreviewPageContainer>
        </PreviewContentWrapper>
      </ModalContent>
    </ModalOverlay>
  );
};

export default PreviewModal;
